import { apiClient } from './client';

// ============================================================================
// Types
// ============================================================================

export interface Permission {
  permission: string;
  description: string;
}

export interface ApiKey {
  id: string;
  name: string;
  keyPrefix: string;
  permissions: string[];
  isActive: boolean;
  lastUsedAt: string | null;
  expiresAt: string | null;
  createdAt: string;
}

export interface CreatedApiKey extends ApiKey {
  key: string; // Full key - only shown once on creation
}

export interface CreateApiKeyInput {
  name: string;
  permissions: string[];
  expiresAt?: string;
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Get available permissions
 */
export async function getPermissions(): Promise<Permission[]> {
  const response = await apiClient.get('/keys/permissions');
  return response.data.data;
}

/**
 * List all API keys for the current user
 */
export async function list(): Promise<ApiKey[]> {
  const response = await apiClient.get('/keys');
  return response.data.data;
}

/**
 * Create a new API key
 */
export async function create(input: CreateApiKeyInput): Promise<CreatedApiKey> {
  const response = await apiClient.post('/keys', input);
  return response.data.data;
}

/**
 * Revoke (delete) an API key
 */
export async function revoke(id: string): Promise<void> {
  await apiClient.delete(`/keys/${id}`);
}

/**
 * Toggle API key active status
 */
export async function toggle(id: string): Promise<{ id: string; isActive: boolean }> {
  const response = await apiClient.post(`/keys/${id}/toggle`);
  return response.data.data;
}

/**
 * Update API key permissions
 */
export async function updatePermissions(id: string, permissions: string[]): Promise<void> {
  await apiClient.put(`/keys/${id}/permissions`, { permissions });
}

// Export as namespace
export const apiKeysApi = {
  getPermissions,
  list,
  create,
  revoke,
  toggle,
  updatePermissions,
};

